import { cn } from '@quadralize/utils'
import type * as React from 'react'

interface EmptyStateProps extends React.HTMLAttributes<HTMLDivElement> {
  icon?: React.ReactNode
  title: string
  description?: string
  action?: React.ReactNode
  variant?: 'default' | 'glass'
}

export function EmptyState({
  icon,
  title,
  description,
  action,
  className,
  variant = 'default',
  ...props
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        'group relative flex grow min-h-90 flex-col items-center justify-center rounded-3xl p-8 text-center transition-all duration-500 overflow-hidden',
        variant === 'default' && 'bg-linear-to-b from-bg-subtle/20 to-bg-base/40',
        variant === 'glass' && 'bg-bg-base/30 backdrop-blur-xl ring-1 ring-white/10 shadow-2xl',
        className,
      )}
      {...props}
    >
      {/* Decorative background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-primary/5 rounded-full blur-[80px] opacity-50 pointer-events-none" />

      <div className="relative z-10 flex flex-col items-center animate-in fade-in zoom-in-95 slide-in-from-bottom-8 duration-1000 ease-out-expo">
        {icon && (
          <div className="relative mb-8">
            {/* Layered ring effects */}
            <div className="absolute inset-0 scale-150 bg-primary/5 rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />

            <div className="relative flex h-20 w-20 items-center justify-center rounded-[28px] bg-bg-base text-text-tertiary shadow-[0_8px_30px_rgb(0,0,0,0.04)] ring-1 ring-border-subtle/50 transition-all duration-500 group-hover:rotate-6 group-hover:scale-110 group-hover:shadow-[0_20px_40px_rgba(0,0,0,0.08)]">
              <div className="h-10 w-10 opacity-70 group-hover:text-primary group-hover:opacity-100 transition-all duration-500">
                {icon}
              </div>
            </div>

            {/* Subtle secondary decoration */}
            <div className="absolute -bottom-1 -right-1 h-6 w-6 rounded-lg bg-bg-base shadow-sm ring-1 ring-border-subtle/30 flex items-center justify-center translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-700 delay-100">
              <div className="h-3 w-3 rounded-full bg-primary/20 animate-pulse" />
            </div>
          </div>
        )}

        <div className="flex flex-col gap-3 px-4">
          <h3 className="text-xl font-bold tracking-tight text-text-primary sm:text-2xl">
            {title}
          </h3>
          {description && (
            <p className="mx-auto max-w-[320px] text-[15px] font-medium leading-relaxed text-text-tertiary">
              {description}
            </p>
          )}
        </div>

        {action && (
          <div className="mt-10 animate-in fade-in slide-in-from-top-4 duration-700 delay-300">
            {action}
          </div>
        )}
      </div>
    </div>
  )
}
